import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, Save, CheckCircle2, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI } from '../../lib/onboardingAPI';

const FIELDS = [
  ['first_name', 'First name', 'text'],
  ['middle_name', 'Middle name', 'text'],
  ['last_name', 'Last name', 'text'],
  ['preferred_name', 'Preferred name', 'text'],
  ['date_of_birth', 'Date of birth', 'date'],
  ['tfn', 'Tax File Number', 'text'],
  ['abn', 'ABN (if applicable)', 'text'],
  ['email', 'Email', 'email'],
  ['mobile', 'Mobile', 'text'],
  ['occupation', 'Occupation', 'text'],
  ['employer_name', 'Employer name', 'text'],
];

const RESIDENCY = ['resident', 'foreign_resident', 'working_holiday_maker', 'temporary_resident'];

export default function PersonalDetailsForm({ clientId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    first_name: '', middle_name: '', last_name: '', preferred_name: '', date_of_birth: '',
    tfn: '', abn: '', email: '', mobile: '', occupation: '', employer_name: '',
    residential_address: '', postal_address: '', residency_status: 'resident',
    australian_resident: true, bank_bsb: '', bank_account_number: '', bank_account_name: '',
    medicare_number: '', private_health_insurance: false, hecs_help_debt: false,
    child_support_obligations: false,
  });

  const [modes, setModes] = useState({});
  useEffect(() => { if (clientId) onboardingAPI.checklist(clientId).then((r) => setModes(r.data.field_modes || {})).catch(() => {}); }, [clientId]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  useEffect(() => {
    if (!clientId) return;
    onboardingAPI.getSection(clientId, 'personal_details')
      .then((res) => setF((s) => ({ ...s, ...(res.data.response_data || {}) })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId]);

  const save = async (status) => {
    setSaving(true);
    try {
      await onboardingAPI.saveSection(clientId, 'personal_details', { response_data: f, status });
      toast.success(status === 'completed' ? 'Personal details completed' : 'Saved — you can return any time');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Personal Details</CardTitle>
          <CardDescription>Tell us about yourself. This populates your client record automatically.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.filter(([k]) => modes[k] !== 'hidden').map(([k, label, type]) => (
              <div key={k} className="space-y-2">
                <Label htmlFor={`pd-${k}`}>{label}{modes[k] === 'required' && <span className="text-destructive" aria-label="required"> *</span>}</Label>
                <Input id={`pd-${k}`} type={type} value={f[k] || ''} onChange={onInput(k)} required={modes[k] === 'required'} data-testid={`pd-${k}`} />
              </div>
            ))}
            <div className="space-y-2">
              <Label>Residency status</Label>
              <Select value={f.residency_status} onValueChange={set('residency_status')}>
                <SelectTrigger data-testid="pd-residency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RESIDENCY.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Residential address</Label>
              <Input value={f.residential_address || ''} onChange={onInput('residential_address')} data-testid="pd-res-address" />
            </div>
            <div className="space-y-2">
              <Label>Postal address</Label>
              <Input value={f.postal_address || ''} onChange={onInput('postal_address')} data-testid="pd-postal-address" />
            </div>
          </div>

          {/* Bank account for refund */}
          <div>
            <p className="text-sm font-semibold text-foreground/90 mb-2">Bank account for refund</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Account name</Label><Input value={f.bank_account_name || ''} onChange={onInput('bank_account_name')} /></div>
              <div className="space-y-2"><Label>BSB</Label><Input value={f.bank_bsb || ''} onChange={onInput('bank_bsb')} /></div>
              <div className="space-y-2"><Label>Account number</Label><Input value={f.bank_account_number || ''} onChange={onInput('bank_account_number')} /></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Medicare number</Label><Input value={f.medicare_number || ''} onChange={onInput('medicare_number')} /></div>
          </div>

          {/* Yes/No toggles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ['australian_resident', 'Australian resident for tax purposes'],
              ['private_health_insurance', 'Has private health insurance'],
              ['hecs_help_debt', 'Has HECS / HELP debt'],
              ['child_support_obligations', 'Has child support obligations'],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 rounded-lg border p-3 cursor-pointer hover:bg-muted/50">
                <Checkbox checked={!!f[k]} onCheckedChange={(v) => set(k)(!!v)} data-testid={`pd-${k}`} />
                <span className="text-sm text-foreground/90">{label}</span>
              </label>
            ))}
          </div>

          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 border rounded-lg p-3">
            <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
            Document uploads (Photo ID, Medicare card, prior-year return, ATO correspondence, bank confirmation)
            will be available in the Document Centre.
          </div>

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => save('in_progress')} disabled={saving} data-testid="pd-save">
              <Save className="w-4 h-4 mr-2" /> Save &amp; return later
            </Button>
            <Button onClick={() => save('completed')} disabled={saving} data-testid="pd-complete">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Mark as complete
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
